import React, { useCallback, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { AlertService } from '@spoiler-alert/ui-library';
import { parseProfileDocument } from './parse';
import { profileDocumentFilename } from './filename';

const ALLOWED_FILE_TYPES = ['application/json'];

const alertWarning = (message) =>
  AlertService.alert({ type: 'warning', message: <span>{message}</span>, autoDismiss: false });

const alertCaution = (message) =>
  AlertService.alert({ type: 'caution', message: <span>{message}</span>, autoDismiss: true, dismissDelay: 5000 });

const alertSuccess = (message) =>
  AlertService.alert({ type: 'success', message: <span>{message}</span>, autoDismiss: true, dismissDelay: 2000 });

const readAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = () => reject(new Error('Failed to read the file.'));
    reader.readAsText(file);
  });

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

// Describes what happened using the server's own counts.
const describeOutcome = ({ created, rowsWritten, rowsRemoved }) =>
  `${created ? 'Created' : 'Updated'} the profile: ${plural(rowsWritten, 'row')} written, ${rowsRemoved} replaced.`;

/**
 * Owns the download/upload lifecycle for one profile manager.
 *
 * The flow is pick file -> parse -> confirm -> write, in that order, so the confirmation modal
 * can state which profile is about to be overwritten and where the file came from.
 */
const useProfileTransfer = ({
  kind,
  identity,
  target,
  buildDocument,
  mutation,
  resultKey,
  onPromoted,
  refetchQueries,
}) => {
  const [pendingUpload, setPendingUpload] = useState(null);
  const [uploadKey, setUploadKey] = useState(0);
  const fileInput = useRef(null);

  const [promote, { loading: uploading }] = useMutation(mutation, { refetchQueries });

  const download = useCallback(() => {
    const exportedAt = new Date().toISOString();
    const profileDocument = buildDocument(exportedAt);
    const blob = new Blob([JSON.stringify(profileDocument, null, 2)], { type: 'application/json' });
    const link = window.document.createElement('a');
    link.download = profileDocumentFilename({
      environment: profileDocument.source.environment,
      siteName: profileDocument.source.siteName,
      kind,
      identity,
      exportedAt,
    });
    link.href = URL.createObjectURL(blob);
    link.click();
    URL.revokeObjectURL(link.href);
  }, [buildDocument, kind, identity]);

  // Opens the picker. Parsing and confirmation happen once a file has been chosen.
  const requestUpload = useCallback(() => {
    if (fileInput.current) fileInput.current.click();
  }, []);

  const handleFileChange = useCallback(
    async (event) => {
      const files = Array.from(event.target.files || []);
      // Rotate the key so choosing the same file twice still fires a change event.
      setUploadKey((key) => key + 1);
      if (!files.length) return;
      if (!files.every((file) => ALLOWED_FILE_TYPES.includes(file.type))) {
        alertWarning('Incorrect file type. Profile files must be JSON.');
        return;
      }

      let text;
      try {
        text = await readAsText(files[0]);
      } catch (error) {
        alertWarning(error.message);
        return;
      }

      const { document: parsed, errors } = parseProfileDocument(text, { kind, identity });
      if (errors) {
        alertWarning(errors.join(' '));
        return;
      }
      setPendingUpload({ document: parsed, filename: files[0].name });
    },
    [kind, identity],
  );

  const cancelUpload = useCallback(() => setPendingUpload(null), []);

  const confirmUpload = useCallback(async () => {
    const upload = pendingUpload;
    setPendingUpload(null);
    if (!upload) return;
    try {
      const { data } = await promote({ variables: { target, document: upload.document } });
      const result = data?.[resultKey];
      if (!result) {
        alertWarning('The upload did not return a result. Refresh and check the profile before retrying.');
        return;
      }
      alertSuccess(describeOutcome(result.summary));
      result.summary.warnings.forEach(alertCaution);
      if (onPromoted) onPromoted(result);
    } catch (error) {
      alertWarning(`Upload failed and nothing was changed: ${error.message}`);
    }
  }, [pendingUpload, promote, target, resultKey, onPromoted]);

  const fileInputProps = {
    type: 'file',
    id: `${kind}-upload`,
    key: `upload-${uploadKey}`,
    onChange: handleFileChange,
    accept: ALLOWED_FILE_TYPES.join(','),
    style: { display: 'none' },
    ref: fileInput,
  };

  return { download, requestUpload, pendingUpload, confirmUpload, cancelUpload, uploading, fileInputProps };
};

export default useProfileTransfer;
